import numeral from 'numeral'
import { useEffect, useCallback } from 'react'
import { toast } from 'react-toastify'

import { Dialog, Layout, Text, Button } from '@loadsmart/miranda-react'

import { formatDescription } from 'core/presenters/stopPresenter'
import { Icon } from 'core/ui/components/icon/Icon'
import { StopsTimeline } from 'core/ui/components/stop/StopsTimeline'
import { formatCurrency } from 'core/utils/currency'

import I18n from 'legacy/components/I18n/i18n'
import { KamionBidBanner } from 'legacy/components/KamionBidBanner/KamionBidBanner'
import { BID_STATUS } from 'legacy/constants/bids'
import { useAnalytics } from 'legacy/containers/LoadsPage/hooks/useAnalytics'
import { getLoad } from 'legacy/services/loads'

import { BookingType } from 'loads/domain/LoadOffer'
import { mapApiToBidLoad } from 'loads/infra/bid/BidLoadMapper'
import { formatZip } from 'loads/presenters/bidLoadStopPresenter'
import { BidExpiredDialog } from 'loads/ui/bid/BidExpiredDialog'
import { BidGauge } from 'loads/ui/bid/BidGauge'
import { useBidProbability } from 'loads/ui/hooks/useBidProbability'
import { useBookingOffer } from 'loads/ui/hooks/useBookingOffer'
import { createPreferredLane } from 'preferred-lanes/domain/PreferredLane'
import { useSuggestedPreferredLanesContext } from 'preferred-lanes/ui/context/suggestedPreferredLanesContext'
import { useSuggestedPreferredLanes } from 'preferred-lanes/ui/hooks/useSuggestedPreferredLanes'

import { BidError } from '../../../../components/Bid/BidError'
import { CONTEXTS } from '../../OfferCard/OfferCard'
import { BidPriceInput, RemoveButton } from './BidDialog.styles'
import { BidEntityTaken } from './BidEntityTaken'
import { BidHistory } from './BidHistory'
import { BidSubmitted } from './BidSubmitted'
import { useBidDialog } from './useBidDialog'
import { useBidHistory } from './useBidHistory'
import { usePrice } from './usePrice'

function BidDialog() {
  const { biddingPrice, biddingPriceError, decreasePrice, increasePrice, setPrice } = usePrice()
  const {
    activeBid,
    bidLoadUUID,
    bidLoad,
    bidOfferUUID,
    biddingBookItNowPrice,
    taken,
    placeBid,
    deleteBid,
    closeDialog,
    bidSubmitted,
    bidError,
    isTaken,
    isLoading,
    isCounterBid,
    isExpired,
    bidProbabilityEnabled,
  } = useBidDialog()
  const { history: bidHistory, fetchBidHistory } = useBidHistory()
  const { analytics } = useAnalytics()
  const { setPreferredLaneSuggestions } = useSuggestedPreferredLanesContext()
  const { trackLaneBid } = useSuggestedPreferredLanes()
  const { navigateToBookOffer } = useBookingOffer()

  const { maxBidPrice, bidProbability } = useBidProbability(
    bidOfferUUID,
    biddingPrice,
    bidProbabilityEnabled
  )

  const hasActiveBid = Boolean(activeBid)
  const formattedBidPrice = numeral(biddingPrice).format('$0,0')

  async function handleBid() {
    const isBidHigher = bidHistory.some(pastBid => Number.parseFloat(pastBid.price) <= biddingPrice)

    if (isBidHigher) {
      toast.error(I18n.get('lb.bidding.dialog.toast.lowerBid'))
      return
    }

    try {
      await placeBid(biddingPrice)

      analytics.tag(['Placed Bid'])
      analytics.trackIntercom(['Placed Bid'])
      analytics.track(['Bid Dialog', 'Place Bid'], {
        biddingPrice,
        biddingBookItNowPrice,
        loadUUID: bidLoadUUID,
        loadMode: bidLoad.mode,
        isCounterBid,
        bidProbabilityEnabled,
        automatedBidResponsesEnabled: true,
      })
    } catch (err) {
      if (err?.code === 410) {
        analytics.tag(['Place Bid Error - Load Gone'])
      } else {
        toast.error(err.message)
        analytics.tag(['Place Bid Error'])
      }
    }

    const { data: load } = await getLoad(bidLoadUUID)
    const preferredLane = createPreferredLane(load.stops, load.equipment_type)
    const suggestions = await trackLaneBid(preferredLane, bidLoadUUID)
    setPreferredLaneSuggestions(suggestions)
  }

  const handleBook = useCallback(() => {
    navigateToBookOffer({
      id: bidLoadUUID,
      triggerContext: CONTEXTS.BID_DIALOG,
      bookingType: BookingType.BookNow,
    })
    analytics.track(['Bid Dialog', 'Book it Now'], {
      loadUUID: bidLoadUUID,
      bidProbabilityEnabled,
      automatedBidResponsesEnabled: true,
    })
    closeDialog()
  }, [navigateToBookOffer, bidLoadUUID, analytics, bidProbabilityEnabled, closeDialog])

  function handleClose() {
    closeDialog()
    analytics.track(['Bid Dialog', 'Close'], {
      loadUUID: bidLoadUUID,
      loadMode: bidLoad.mode,
      bidProbabilityEnabled,
      automatedBidResponsesEnabled: true,
    })
  }

  async function handleDelete() {
    try {
      await deleteBid(activeBid)
      analytics.track(['Bid Dialog', 'Remove'], {
        biddingBookItNowPrice,
        biddingPrice,
        loadUUID: bidLoadUUID,
        loadMode: bidLoad.mode,
      })
      analytics.tag(['Delete Bid'])
    } catch {
      analytics.tag(['Delete Bid Error'])
    }
  }

  useEffect(() => {
    // Restores price from an active bid or default value to book it now price
    if (activeBid) {
      setPrice(activeBid.price)
    } else if (biddingBookItNowPrice) {
      setPrice(biddingBookItNowPrice)
    }
  }, [activeBid, biddingBookItNowPrice, setPrice])

  useEffect(() => {
    analytics.track(['Bid Dialog', 'View'], {
      loadUUID: bidLoadUUID,
      loadMode: bidLoad.mode,
      bidProbabilityEnabled,
      automatedBidResponsesEnabled: true,
    })
  }, [bidLoadUUID, bidLoad, analytics, bidProbabilityEnabled])

  useEffect(() => {
    fetchBidHistory(bidOfferUUID, { status: BID_STATUS.REFUSED })
  }, [bidOfferUUID, fetchBidHistory])

  const { stops } = mapApiToBidLoad(bidLoad)

  const mainContent = (
    <>
      <Dialog.Header>
        <Text variant="heading-md-bold" color="color-secondary-60">
          {hasActiveBid ? 'Update your bid' : 'Place your bid'}
        </Text>
        <Dialog.Close data-testid="close" onClick={handleClose} />
      </Dialog.Header>
      <Dialog.Body>
        <Layout.Stack gap="spacing-6">
          <Text variant="body-md" color="color-neutral-60">
            After placing your bid, you will receive an immediate response. If your bid is
            successful, you will be able to accept or decline the load.
          </Text>
          {Boolean(stops.length) && (
            <StopsTimeline
              stops={stops}
              titleFormatter={formatDescription}
              subTitleFormatter={formatZip}
            />
          )}
          <Layout.Group gap="spacing-0" align="center" justify="center">
            <Button onClick={decreasePrice} scale="default" variant="icon">
              <Icon name="minus" />
            </Button>
            <BidPriceInput
              autoFocus
              type="currency"
              value={formattedBidPrice}
              onChange={evt => setPrice(evt.target.value)}
              hasError={biddingPriceError}
              aria-label="bid value"
              role="textbox"
              name="bid value"
            />
            <Button onClick={increasePrice} scale="default" variant="icon">
              <Icon name="plus" />
            </Button>
          </Layout.Group>
          {bidProbabilityEnabled && (
            <BidGauge
              probability={bidProbability}
              minBidValue={biddingBookItNowPrice}
              maxBidValue={maxBidPrice < biddingBookItNowPrice ? undefined : maxBidPrice} // edge case where load is already at it lowest price
            />
          )}
          <BidHistory title={I18n.get('lb.bidding.dialog.lastRejectedBids')} history={bidHistory} />
        </Layout.Stack>
      </Dialog.Body>
      <Dialog.Footer>
        <Layout.Group justify="flex-end">
          <Dialog.ActionSecondary disabled={isLoading} onClick={handleBook}>
            {`${I18n.get('lb.bidding.dialog.action.bookNow')} ${formatCurrency(
              biddingBookItNowPrice
            )}`}
          </Dialog.ActionSecondary>
          <Dialog.ActionPrimary disabled={biddingPriceError || isLoading} onClick={handleBid}>
            {hasActiveBid
              ? I18n.get('lb.bidding.dialog.action.updateBid')
              : I18n.get('lb.bidding.dialog.action.placeBid')}
          </Dialog.ActionPrimary>
          {hasActiveBid && (
            <RemoveButton variant="tertiary" scale="small" onClick={handleDelete}>
              {I18n.get('lb.bidding.dialog.action.removeBid')}
            </RemoveButton>
          )}
        </Layout.Group>
      </Dialog.Footer>
    </>
  )

  function renderContent() {
    if (isLoading || bidSubmitted) {
      return <BidSubmitted handleClose={handleClose} />
    }

    if (isExpired) {
      return <BidExpiredDialog onClose={handleClose} />
    }

    if (bidError) {
      return <BidError onClose={handleClose} />
    }

    if (isTaken) {
      return <BidEntityTaken entity={taken} handleClose={handleClose} />
    }

    return mainContent
  }

  return (
    <>
      {renderContent()}
      {bidSubmitted && <KamionBidBanner />}
    </>
  )
}

export { BidDialog }
